import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../components/common/PageHeader";
import SearchInput from "../../components/search/SearchInput";
import UserSearchResults from "../../components/search/UserSearchResults";
import { useUserSearch } from "../../hooks/user/useUserSearch";

/**
 * Dedicated search page for user search
 *
 * Features:
 * - Full search experience with pagination
 * - URL query parameter support (?q=search)
 * - Back navigation
 * - Responsive design
 */
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [page, setPage] = useState(1);

  // Fetch search results
  const { data, isLoading, isError, error } = useUserSearch(query, {
    page,
    limit: 20,
  });

  const users = data?.users || [];
  const pagination = data?.pagination || {};
  const { total = 0, totalPages = 0, currentPage = 1 } = pagination;

  // Update URL when query changes
  useEffect(() => {
    if (query.trim()) {
      setSearchParams({ q: query });
    } else {
      setSearchParams({});
    }
  }, [query, setSearchParams]);

  // Sync query from URL on mount
  useEffect(() => {
    const urlQuery = searchParams.get("q");
    if (urlQuery && urlQuery !== query) {
      setQuery(urlQuery);
    }
  }, [searchParams, query]);

  // Handle search input change
  const handleSearchChange = (newQuery) => {
    setQuery(newQuery);
    setPage(1); // Reset to first page on new search
  };

  // Handle user click - navigate to profile
  const handleUserClick = (user) => {
    const userId = user._id || user.id;
    if (userId) {
      navigate(`/users/${userId}`);
    }
  };

  // Handle back navigation
  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  // Handle pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div>
      <PageHeader
        title="Search Users"
        subtitle="Find and connect with other users"
        icon={faSearch}
        backgroundIcon={faSearch}
      />

      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleBackClick}
          className="inline-flex items-center gap-2 px-4 py-2 text-medium-grey hover:text-dark-grey transition-colors duration-200 bg-white rounded-md shadow-sm hover:shadow-md"
          aria-label="Go back"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <SearchInput
          value={query}
          onChange={handleSearchChange}
          onClear={() => setQuery("")}
          placeholder="Search by username or name..."
          className="max-w-2xl mx-auto"
          inputClassName="text-base py-3"
        />

        {/* Results Count */}
        {query.trim().length >= 2 && !isLoading && (
          <div className="text-center mt-4 text-sm text-medium-grey">
            {total === 0
              ? "No results found"
              : `Found ${total} result${total !== 1 ? "s" : ""}`}
          </div>
        )}
      </div>

      {/* Search Results */}
      <div className="bg-white rounded-card shadow-card p-6">
        <UserSearchResults
          users={users}
          isLoading={isLoading}
          isError={isError}
          error={error}
          query={query}
          compact={false}
          onUserClick={handleUserClick}
        />

        {/* Pagination */}
        {!isLoading && !isError && totalPages > 1 && (
          <div className="mt-6 pt-6 border-t border-light-grey">
            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`
                  px-4 py-2 rounded-md font-medium transition-colors
                  ${
                    currentPage === 1
                      ? "bg-light-grey text-medium-grey cursor-not-allowed"
                      : "bg-gold text-white hover:bg-deep-red"
                  }
                `}
              >
                Previous
              </button>

              {/* Page Info */}
              <div className="text-sm text-medium-grey">
                Page {currentPage} of {totalPages}
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`
                  px-4 py-2 rounded-md font-medium transition-colors
                  ${
                    currentPage === totalPages
                      ? "bg-light-grey text-medium-grey cursor-not-allowed"
                      : "bg-gold text-white hover:bg-deep-red"
                  }
                `}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
